import React, { useEffect, useState } from "react";
import {
  Container,
  Row,
  Col,
  Card,
  CardBody,
  Button,
  Form,
  FormGroup,
  Label,
  Input,
} from "reactstrap";
import Select from "react-select";
import Breadcrumbs from "../../../../components/Common/Breadcrumb";
import TableContainer from "../../../../components/Common/TableContainer";
import { useFormik } from "formik";
import * as Yup from "yup";
import { genderOptions } from "common/data/constants";
import CustomDatePicker from "common/CustomDatePicker";
import { useDispatch, useSelector } from "react-redux";
import { fetchCategories, fetchEmployeeDropdown, fetchPermissionPolicyRequest, savePermissionPolicyRequest } from "store/actions";
import { useAuthUserDetails } from "components/Common/getAuthUserDetails";
import { useLocation,useHistory } from "react-router-dom";
import getAccessRights from "components/Common/getAccessRights";
import useButtonAccess from "components/Common/useButtonAccess";
import Swal from "sweetalert2";

const PermissionPolicy = () => {
  const [accessRight, setAccessRight] = useState(null);
  const location = useLocation();
  const history = useHistory();
  useEffect(() => {
      const rights = getAccessRights(location.pathname, history);
      setAccessRight(rights?.data);
  }, [location]);
  const { canRead, canCreate, canEdit, canDelete } = useButtonAccess(accessRight);
  const dispatch = useDispatch();
  const [authDetails, setAuthDetails] = useState(null);
  const {
    categories,
    dropdownData,
    per_policy_list,
    loading,
    error,
    success
  } = useSelector((state) => ({
    categories: state.CategoryMaster?.categories ?? [],
    dropdownData: state.EmployeeDetailsReducer?.dropdownData ?? [],
    per_policy_list: state.EmployeePermissionPolicyReducer?.per_policy_list ?? [],

    loading: state.CategoryMaster?.loading || state.EmployeeDetailsReducer?.loading || state.EmployeePermissionPolicyReducer?.loading,
    error: state.CategoryMaster?.error || state.EmployeeDetailsReducer?.error || state.EmployeePermissionPolicyReducer?.error,
    success: state.EmployeePermissionPolicyReducer?.success,
  }));
  const { empment_type } = dropdownData;

  useEffect(() => {
    const authUserDetails = useAuthUserDetails();
    setAuthDetails(authUserDetails[0])
    const unitId = authUserDetails[0].unit_id
    dispatch(fetchCategories(unitId));
    dispatch(fetchEmployeeDropdown(unitId))
    dispatch(fetchPermissionPolicyRequest(unitId))
  }, [dispatch])

  useEffect(() => {
    if (success) {
      formik.resetForm();
      dispatch(fetchPermissionPolicyRequest(authDetails?.unit_id));
    }
  }, [authDetails, success, dispatch]);

  const formik = useFormik({
    initialValues: {
      permissionType: "For the Period",
      permissionFrom: "",
      permissionTo: "",
      category: "",
      employeeType: "",
      gender: "",
      eligibleCount: "",
      perDayCount: "",
      eachPermission: "",
      allowLateEntry: "",
      appliedBeforeDay: "",
      showInDashboard: "",
      bankHrUsed: "",
    },
    validationSchema: Yup.object({
      permissionType: Yup.string().required("Permission Type is required"),
      permissionFrom: Yup.date()
        .when("permissionType", {
          is: "For the Period",
          then: (schema) => schema.required("Permission From is required"),
          otherwise: (schema) => schema.nullable(),
        }),

      permissionTo: Yup.date()
        .when("permissionType", {
          is: "For the Period",
          then: (schema) =>
            schema
              .required("Permission To is required")
              .min(Yup.ref("permissionFrom"), "Permission To must be after Permission From"),
          otherwise: (schema) => schema.nullable(),
        }),
      category: Yup.object().required("Category is required"),
      employeeType: Yup.object().required("Employee Type is required"),
      gender: Yup.string().required("Gender is required"),
      eligibleCount: Yup.number().required("Eligible Permission Count is required").min(0, "Must be at least 0").max(120,"Must be atleast 120"),
      eachPermission: Yup.number().required("Each Permission (Minutes) is required").min(0, "Must be at least 0").max(120,"Must be atleast 120"),
      allowLateEntry: Yup.string().required("Allow Late Entry is required"),
      // appliedBeforeDay: Yup.number().required("To be applied Before Day is required").min(0, "Must be at least 0"),
      appliedBeforeDay: Yup.number()
      .when("allowLateEntry", {
        is: "No", // when No, it's required
        then: Yup.number()
          .typeError("Must be a number")
          .required("To be applied before day is required").min(0, "Must be at least 0").max(99,"Must be atleast 99")
          .positive("Must be greater than 0"),
        otherwise: Yup.number().nullable(), // when Yes, not required
      }),
      showInDashboard: Yup.string().required("Show in Dashboard is required"),
      bankHrUsed: Yup.string().required("Is Bank Hr Used for Permission is required"),
      perDayCount: Yup.number().required("Per Day Count is required").min(0, "Must be at least 0").max(99,"Must be at least 99"),
    }),
    validateOnChange: true,  // <-- Ensure validation on change
    validateOnBlur: true,    // <-- Ensure validation on blur
    onSubmit: (values) => {
      console.log('values > ', values)

      const formData = new FormData();

      formData.append("per_policy_id", 0);
      formData.append(
        "per_type",
        values.permissionType === "For the Period" ? 1 :
          values.permissionType === "For a Month" ? 2 :
            0
      );

      formData.append("from_date", values.permissionFrom);
      formData.append("to_date", values.permissionTo);

      formData.append("cat_id", values.category?.value);
      formData.append("emp_type_id", values.employeeType?.value);
      formData.append("gender_id", values.gender);

      formData.append("permission_count", values.eligibleCount);
      formData.append("each_permission", values.eachPermission);
      formData.append("per_day_count", values.perDayCount);

      formData.append("allow_late_entry", values.allowLateEntry === "Yes" ? "1" : "0");
      formData.append("to_be_applied_beforedays", values.appliedBeforeDay);

      formData.append("is_dashboard", values.showInDashboard === "Yes" ? "1" : "0");
      formData.append("is_bankhrs", values.bankHrUsed === "Yes" ? "1" : "0");

      formData.append("create_by", authDetails.user_id);
      formData.append("unit_id", authDetails.unit_id);

      dispatch(savePermissionPolicyRequest(formData))
    },
  });


  const columns = [
    { Header: "S.No", className: "serial-no",applyHeaderClass: true, Cell: ({ row }) => per_policy_list.length - row.index },
    { Header: "Permission Type", accessor: "permission_type" },
    { Header: "Permission From", accessor: "from_date" },
    { Header: "Permission To", accessor: "to_date" },
    { Header: "Category", accessor: "category" },
    { Header: "Employee Type", accessor: "emp_type" },
    { Header: "Gender", accessor: "gender_type" },
    { Header: "Eligible Count", accessor: "eligible_count" },
    { Header: "Per Day Count", accessor: "per_day_count" },
    { Header: "Each Permission (Minutes)", accessor: "each_permission" },
    { Header: "Allow Late Entry", accessor: "allow_late_entry" },
    { Header: "Applied Before Day", accessor: "applied_beforeday" },
    { Header: "Show in Dashboard", accessor: "is_dashboard" },
    { Header: "Bank Hr Used for Permission", accessor: "is_bankhrs" },
  ];

  // select custome style
  const SelectcustomStyles = {
    control: (base) => ({
      ...base,
      maxheight: 33.1,
      minHeight: 33.1,
      // overflow: "scroll",
    }),
    indicatorsContainer: (base) => ({
      ...base,
      padding: "0", // Match the padding for both dropdown and clear indicator
    }),
    dropdownIndicator: (base) => ({
      ...base,
      padding: "2px 8px", // Ensure consistent padding for dropdown indicator
    }),
    clearIndicator: (base) => ({
      ...base,
      padding: "2px 8px", // Adjust padding for clear indicator to match dropdown
    }),
  };

  const handleCancel = async (e) => {
    e.preventDefault();

    // If no changes made, reset directly
    if (!formik.dirty) {
      formik.resetForm();
      return;
    }

    const result = await Swal.fire({
      title: "Confirm Cancel",
      text: "All entered data will be cleared. Do you want to continue?",
      icon: "warning",
      showCancelButton: true,
      confirmButtonText: "Confirm",
      cancelButtonText: "Cancel",
      confirmButtonColor: "rgb(145, 73, 234)"
      ,
      cancelButtonColor: "#6c757d",
    });

    if (result.isConfirmed) {
      formik.resetForm();

      // Optional success message
      Swal.fire({
        icon: "success",
        title: "Cancelled",
        text: "Form data has been cleared",
        timer: 1500,
        showConfirmButton: false,
      });
    }
  };



  return (
    <React.Fragment>
      <div className="page-content">
        <Container fluid>
          <Breadcrumbs title="Policy Setup" breadcrumbItem="Permission Policy" />
          {(canRead && canCreate) && (
          <Card>
            <CardBody>
              <Form onSubmit={formik.handleSubmit}>
                <Row className="gy-3">
                  {/* Radio Buttons for Permission Type */}
                  <Col md="4">
                    <FormGroup>
                      <Label>Permission</Label>
                      <div className="d-flex gap-4">
                        <FormGroup check>
                          <Input
                            type="radio"
                            name="permissionType"
                            value="For the Period"
                            checked={formik.values.permissionType === "For the Period"}
                            onChange={formik.handleChange}
                            onBlur={formik.handleBlur}
                          />
                          <Label check>For the Period</Label>
                        </FormGroup>
                        <FormGroup check>
                          <Input
                            type="radio"
                            name="permissionType"
                            value="For a Month"
                            checked={formik.values.permissionType === "For a Month"}
                            onChange={formik.handleChange}
                            onBlur={formik.handleBlur}
                          />
                          <Label check>For a Month</Label>
                        </FormGroup>
                      </div>
                      {formik.touched.permissionType && formik.errors.permissionType ? (
                        <div className="text-danger">{formik.errors.permissionType}</div> // <-- Show error
                      ) : null}
                    </FormGroup>
                  </Col>
                  {formik.values.permissionType === "For the Period" && (
                    <>
                      <Col md="4">
                        <FormGroup>
                          <Label>Permission From <span className="text-danger">*</span></Label>
                          <CustomDatePicker
                            value={formik.values.permissionFrom}
                            onChange={(date) => formik.setFieldValue("permissionFrom", date)}
                            onBlur={() => formik.setFieldTouched("permissionFrom", true)}
                          />
                          {formik.touched.permissionFrom && formik.errors.permissionFrom ? (
                            <div className="text-danger">{formik.errors.permissionFrom}</div>
                          ) : null}
                        </FormGroup>
                      </Col>
                      <Col md="4">
                        <FormGroup>
                          <Label>Permission To <span className="text-danger">*</span></Label>
                          <CustomDatePicker
                            value={formik.values.permissionTo}
                            onChange={(date) => formik.setFieldValue("permissionTo", date)}
                            onBlur={() => formik.setFieldTouched("permissionTo", true)}
                          />
                          {formik.touched.permissionTo && formik.errors.permissionTo ? (
                            <div className="text-danger">{formik.errors.permissionTo}</div>
                          ) : null}
                        </FormGroup>
                      </Col>
                    </>
                  )}
                  <Col md="4">
                    <FormGroup>
                      <Label>Category <span className="text-danger">*</span></Label>
                      <Select
                        styles={SelectcustomStyles}
                        options={[
                          { value: "0", label: "All" }, // Added All option
                          ...categories.map(cat => ({ value: cat.empCatgryid.toString(), label: cat.empCategory })),
                        ]}
                        value={formik.values.category || null}
                        onChange={(selectedOption) => formik.setFieldValue("category", selectedOption)}
                        onBlur={formik.handleBlur}
                        className={`hrms-select ${formik.touched.category && formik.errors.category ? "is-invalid" : ""}`}
                        classNamePrefix="hrms-select"
                      />
                      {formik.touched.category && formik.errors.category ? (
                        <div className="text-danger">{formik.errors.category}</div>
                      ) : null}
                    </FormGroup>
                  </Col>

                  <Col md="4">
                    <FormGroup>
                      <Label>Employee Type <span className="text-danger">*</span></Label>
                      <Select
                        styles={SelectcustomStyles}
                        options={[
                          { value: "0", label: "All" }, // Added All option
                          ...(empment_type || []).map(cat => ({ value: cat.emptype_id.toString(), label: cat.emp_type })),
                        ]}
                        value={formik.values.employeeType || null}
                        onChange={(selectedOption) => formik.setFieldValue("employeeType", selectedOption)}
                        onBlur={formik.handleBlur}
                        className={`hrms-select ${formik.touched.employeeType && formik.errors.employeeType ? "is-invalid" : ""}`}
                        classNamePrefix="hrms-select"
                      />
                      {formik.touched.employeeType && formik.errors.employeeType ? (
                        <div className="text-danger">{formik.errors.employeeType}</div>
                      ) : null}
                    </FormGroup>
                  </Col>

                  <Col md="4">
                    <FormGroup>
                      <Label>Gender <span className="text-danger">*</span></Label>
                      <Select
                        styles={SelectcustomStyles}
                        options={[
                          { value: "0", label: "All" }, // Added All option
                          ...genderOptions,
                        ]}
                        className="hrms-select"
                        classNamePrefix="hrms-select"
                        value={
                          [{ value: "0", label: "All" }, ...genderOptions].find(
                            (option) => option.value === formik.values.gender
                          ) || null
                        }
                        onChange={(selectedOption) =>
                          formik.setFieldValue("gender", selectedOption?.value || "")
                        }
                      />
                      {formik.touched.gender && formik.errors.gender ? (
                        <div className="text-danger">{formik.errors.gender}</div>
                      ) : null}
                    </FormGroup>
                  </Col>
                  <Col md="4">
                    <FormGroup>
                      <Label>Eligible Permission Count <span className="text-danger">*</span></Label>
                      <Input
                        type="number"
                        name="eligibleCount"
                        maxLength={2}
                        value={formik.values.eligibleCount}
                        onChange={formik.handleChange}
                        onBlur={formik.handleBlur}
                      />
                      {formik.touched.eligibleCount && formik.errors.eligibleCount ? (
                        <div className="text-danger">{formik.errors.eligibleCount}</div> // <-- Show error
                      ) : null}
                    </FormGroup>
                  </Col>
                  <Col md="4">
                    <FormGroup>
                      <Label>Each Permission (Minutes) <span className="text-danger">*</span></Label>
                      <Input
                        type="number"
                        name="eachPermission"
                        maxLength={3}
                        value={formik.values.eachPermission}
                        onChange={formik.handleChange}
                        onBlur={formik.handleBlur}
                      />
                      {formik.touched.eachPermission && formik.errors.eachPermission ? (
                        <div className="text-danger">{formik.errors.eachPermission}</div> // <-- Show error
                      ) : null}
                    </FormGroup>
                  </Col>
                  <Col md="4">
                    <FormGroup>
                      <Label>Per Day Count <span className="text-danger">*</span></Label>
                      <Input
                        type="number"
                        name="perDayCount"
                        maxLength={2}
                        value={formik.values.perDayCount}
                        onChange={formik.handleChange}
                        onBlur={formik.handleBlur}
                      />
                      {formik.touched.perDayCount && formik.errors.perDayCount ? (
                        <div className="text-danger">{formik.errors.perDayCount}</div> // <-- Show error
                      ) : null}
                    </FormGroup>
                  </Col>

                  <Col md="4">
                    <FormGroup>
                       <Label>
                        To be applied Before Day{" "}
                        {formik.values.allowLateEntry === "No" && (
                          <span className="text-danger">*</span>
                        )}
                      </Label>
                      <Input
                        type="number"
                        name="appliedBeforeDay"
                        maxLength={2}
                        value={formik.values.appliedBeforeDay}
                        onChange={formik.handleChange}
                        onBlur={formik.handleBlur}
                      />
                      {formik.touched.appliedBeforeDay && formik.errors.appliedBeforeDay ? (
                        <div className="text-danger">{formik.errors.appliedBeforeDay}</div> // <-- Show error
                      ) : null}
                    </FormGroup>
                  </Col>
                  <Col md="4">
                    <FormGroup>
                      <Label>Allow Late Entry <span className="text-danger">*</span></Label>
                      <div className="d-flex gap-4">
                        <FormGroup check>
                          <Input
                            type="radio"
                            name="allowLateEntry"
                            value="Yes"
                            checked={formik.values.allowLateEntry === "Yes"}
                            onChange={formik.handleChange}
                            onBlur={formik.handleBlur}
                          />
                          <Label check>Yes</Label>
                        </FormGroup>
                        <FormGroup check>
                          <Input
                            type="radio"
                            name="allowLateEntry"
                            value="No"
                            checked={formik.values.allowLateEntry === "No"}
                            onChange={formik.handleChange}
                            onBlur={formik.handleBlur}
                          />
                          <Label check>No</Label>
                        </FormGroup>
                      </div>
                      {formik.touched.allowLateEntry && formik.errors.allowLateEntry ? (
                        <div className="text-danger">{formik.errors.allowLateEntry}</div> // <-- Show error
                      ) : null}
                    </FormGroup>
                  </Col>
                  <Col md="4">
                    <FormGroup>
                      <Label>Show in Dashboard <span className="text-danger">*</span></Label>
                      <div className="d-flex gap-4">
                        <FormGroup check>
                          <Input
                            type="radio"
                            name="showInDashboard"
                            value="Yes"
                            checked={formik.values.showInDashboard === "Yes"}
                            onChange={formik.handleChange}
                            onBlur={formik.handleBlur}
                          />
                          <Label check>Yes</Label>
                        </FormGroup>
                        <FormGroup check>
                          <Input
                            type="radio"
                            name="showInDashboard"
                            value="No"
                            checked={formik.values.showInDashboard === "No"}
                            onChange={formik.handleChange}
                            onBlur={formik.handleBlur}
                          />
                          <Label check>No</Label>
                        </FormGroup>
                      </div>
                      {formik.touched.showInDashboard && formik.errors.showInDashboard ? (
                        <div className="text-danger">{formik.errors.showInDashboard}</div> // <-- Show error
                      ) : null}
                    </FormGroup>
                  </Col>

                  <Col md="4">
                    <FormGroup>
                      <Label>Is Bank Hr Used for Permission <span className="text-danger">*</span></Label>
                      <div className="d-flex gap-4">
                        <FormGroup check>
                          <Input
                            type="radio"
                            name="bankHrUsed"
                            value="Yes"
                            checked={formik.values.bankHrUsed === "Yes"}
                            onChange={formik.handleChange}
                            onBlur={formik.handleBlur}
                          />
                          <Label check>Yes</Label>
                        </FormGroup>
                        <FormGroup check>
                          <Input
                            type="radio"
                            name="bankHrUsed"
                            value="No"
                            checked={formik.values.bankHrUsed === "No"}
                            onChange={formik.handleChange}
                            onBlur={formik.handleBlur}
                          />
                          <Label check>No</Label>
                        </FormGroup>
                      </div>
                      {formik.touched.bankHrUsed && formik.errors.bankHrUsed ? (
                        <div className="text-danger">{formik.errors.bankHrUsed}</div> // <-- Show error
                      ) : null}
                    </FormGroup>
                  </Col>


                </Row>

                <div className="text-end mt-3 mob-center">
                  <Button type="submit" className="btn button-save  me-2">
                    <i className="fas fa-save me-1" /> Save
                  </Button>
                  <Button
                    type="button"
                    onClick={handleCancel} className="btn button-cancel"> <i className="fas fa-sync me-1" /> Cancel
                  </Button>
                </div>
              </Form>
            </CardBody>
          </Card>
          )}

          {/* Table Section */}
          {(canRead) && (
          <Card className="mt-4">
            <CardBody>
              <TableContainer
                columns={columns}
                data={per_policy_list}
                isGlobalFilter={false}
                customPageSize={10}
              />
            </CardBody>
          </Card>
          )}
        </Container>
      </div>
    </React.Fragment>
  );
};

export default PermissionPolicy;